import { PACKAGE_TYPE } from "constants/packageOptions.constants";
import { PALLET_TYPE } from "constants/palletOptions.constants";
import { PackageSettings } from "models/packageSettings";
import { CargoRow, Group as UiGroup, PalletRow } from "models/packingList";
import { createDefaultPackageSettings, createDefaultPalletRow } from "store/reducers/packingList/packingList.helpers";
import { BoxRollType, Group as CloGroup, PalleteType, ProductType } from "store/reducers/root/root.types";
import { pallet_types } from "utils/data";

const PACKAGE_TYPE_TO_CLO: Record<string, BoxRollType> = {
  [PACKAGE_TYPE.BOX]: "box",
  [PACKAGE_TYPE.BIG_BAGS]: "bigbags",
  [PACKAGE_TYPE.BARRELS]: "barrels",
  [PACKAGE_TYPE.SACKS]: "sacks",
  [PACKAGE_TYPE.PIPES]: "pipes",
  [PACKAGE_TYPE.ROLLS]: "roll",
};

const CLO_TYPE_TO_PACKAGE: Record<string, CargoRow["packageType"]> = {
  box: PACKAGE_TYPE.BOX,
  bigbags: PACKAGE_TYPE.BIG_BAGS,
  barrels: PACKAGE_TYPE.BARRELS,
  sacks: PACKAGE_TYPE.SACKS,
  pipes: PACKAGE_TYPE.PIPES,
  roll: PACKAGE_TYPE.ROLLS,
  bulk: PACKAGE_TYPE.BOX,
};

const PALLET_TYPE_TO_CLO: Record<string, string> = {
  [PALLET_TYPE.EU_PALLETS]: "euro",
  [PALLET_TYPE.EU_HALF_PALLETS]: "euroHalf",
  [PALLET_TYPE.EU_PLASTIC_PALLETS]: "euroPlastic",
  [PALLET_TYPE.US_PALLETS]: "us",
  [PALLET_TYPE.CUSTOM_PALLETS]: "custom",
};

const CLO_TYPE_TO_PALLET: Record<string, string> = {
  euro: PALLET_TYPE.EU_PALLETS,
  euroHalf: PALLET_TYPE.EU_HALF_PALLETS,
  euroPlastic: PALLET_TYPE.EU_PLASTIC_PALLETS,
  us: PALLET_TYPE.US_PALLETS,
  custom: PALLET_TYPE.CUSTOM_PALLETS,
};

const enabledValue = (enabled: boolean | undefined, value: number | null | undefined): number | null =>
  enabled ? (value ?? 0) : null;

const packageSettingsToStacking = (
  settings: PackageSettings,
  isStuffingDisabled?: boolean
): ProductType["stacking"] & { isStuffingDisabled?: boolean } => {
  const base = {
    layers: null as number | null,
    topWeight: null as number | null,
    height: null as number | null,
    fill: null as number | null,
    rollPlacement: null as number | null,
  };

  switch (settings.type) {
    case PACKAGE_TYPE.BOX:
      return {
        ...base,
        tiltX: settings.tiltToLength,
        tiltY: settings.tiltToWidth,
        layers: enabledValue(settings.layersCount.enabled, settings.layersCount.value),
        topWeight: enabledValue(settings.mass.enabled, settings.mass.value),
        height: enabledValue(settings.heightStuffing.enabled, settings.heightStuffing.value),
        fill: enabledValue(settings.fillRatio.enabled, settings.fillRatio.value),
      };
    case PACKAGE_TYPE.BIG_BAGS:
    case PACKAGE_TYPE.SACKS:
      return {
        ...base,
        tiltX: settings.tiltToLength,
        tiltY: settings.tiltToWidth,
        layers: enabledValue(settings.layersCount.enabled, settings.layersCount.value),
        topWeight: enabledValue(settings.mass.enabled, settings.mass.value),
        height: enabledValue(settings.heightStuffing.enabled, settings.heightStuffing.value),
        isStuffingDisabled: settings.disableStacking,
      };
    case PACKAGE_TYPE.BARRELS:
    case PACKAGE_TYPE.ROLLS:
    case PACKAGE_TYPE.PIPES:
      return {
        ...base,
        tilt: settings.tilt,
        layers: enabledValue(settings.layersCount.enabled, settings.layersCount.value),
        topWeight: enabledValue(settings.mass.enabled, settings.mass.value),
        height: enabledValue(settings.heightStuffing.enabled, settings.heightStuffing.value),
        isStuffingDisabled: settings.disableStacking,
      };
    default:
      return { ...base, isStuffingDisabled };
  }
};

const stackingToPackageSettings = (
  packageType: CargoRow["packageType"],
  stacking: ProductType["stacking"],
  isStuffingDisabled?: boolean
): PackageSettings => {
  const defaults = createDefaultPackageSettings(packageType);
  const withToggle = <T extends { enabled: boolean; value: number | null }>(
    field: T | undefined,
    value: number | null | undefined
  ): T =>
    ({
      ...(field ?? { enabled: false, value: 0 }),
      enabled: value != null,
      value: value ?? 0,
    }) as T;

  switch (defaults.type) {
    case PACKAGE_TYPE.BOX:
      return {
        ...defaults,
        tiltToLength: Boolean(stacking.tiltX),
        tiltToWidth: Boolean(stacking.tiltY),
        layersCount: withToggle(defaults.layersCount, stacking.layers),
        mass: withToggle(defaults.mass, stacking.topWeight),
        heightStuffing: withToggle(defaults.heightStuffing, stacking.height),
        fillRatio: withToggle(defaults.fillRatio, stacking.fill),
      };
    case PACKAGE_TYPE.BIG_BAGS:
    case PACKAGE_TYPE.SACKS:
      return {
        ...defaults,
        tiltToLength: Boolean(stacking.tiltX),
        tiltToWidth: Boolean(stacking.tiltY),
        layersCount: withToggle(defaults.layersCount, stacking.layers),
        mass: withToggle(defaults.mass, stacking.topWeight),
        heightStuffing: withToggle(defaults.heightStuffing, stacking.height),
        disableStacking: Boolean(isStuffingDisabled),
      };
    case PACKAGE_TYPE.BARRELS:
    case PACKAGE_TYPE.ROLLS:
    case PACKAGE_TYPE.PIPES:
      return {
        ...defaults,
        tilt: Boolean(stacking.tilt),
        layersCount: withToggle(defaults.layersCount, stacking.layers),
        mass: withToggle(defaults.mass, stacking.topWeight),
        heightStuffing: withToggle(defaults.heightStuffing, stacking.height),
        disableStacking: Boolean(isStuffingDisabled),
      };
    default:
      return defaults;
  }
};

export const cloProductToCargoRow = (item: ProductType): CargoRow => {
  const packageType = CLO_TYPE_TO_PACKAGE[item.type] ?? PACKAGE_TYPE.BOX;
  return {
    id: item.uid,
    product: item.name,
    packageType,
    length: item.size.length,
    width: item.size.width,
    height: item.size.height,
    weight: item.weight,
    qty: item.qty,
    color: String(item.color ?? "#000"),
    packageSettings: stackingToPackageSettings(packageType, item.stacking, item.isStuffingDisabled),
  };
};

export const cargoRowToCloProduct = (row: CargoRow, index: number): ProductType => {
  const type = PACKAGE_TYPE_TO_CLO[row.packageType] ?? "box";
  const stackingMeta = packageSettingsToStacking(row.packageSettings);
  const { isStuffingDisabled, ...stacking } = stackingMeta;

  const length = row.length ?? 0;
  const width = row.width ?? 0;
  const height = row.height ?? 0;
  const isRound = type === "barrels" || type === "roll" || type === "pipes";

  return {
    uid: row.id,
    name: row.product,
    type,
    qty: row.qty ?? 0,
    weight: row.weight,
    color: row.color,
    index: index + 1,
    size: {
      length,
      width,
      height,
      radius: isRound ? width / 2 : null,
      diameter: isRound ? width : null,
    },
    stacking,
    ...(isStuffingDisabled != null ? { isStuffingDisabled } : {}),
  };
};

export const cloPalletToPalletRow = (pallet: PalleteType): PalletRow => ({
  id: pallet.uid,
  palletType: CLO_TYPE_TO_PALLET[pallet.type] ?? pallet.type,
  palleteName: pallet.name,
  length: pallet.size.length,
  width: pallet.size.width,
  height: pallet.size.height,
  depth: pallet.depth,
  maxWeight: pallet.maxWeight,
  color: String(pallet.color ?? "#000"),
});

export const palletRowToCloPallet = (row: PalletRow): PalleteType => {
  const cloType = PALLET_TYPE_TO_CLO[row.palletType] ?? "custom";
  const preset = pallet_types[cloType] ?? pallet_types.custom ?? pallet_types.euro;

  return {
    uid: row.id,
    color: row.color,
    depth: row.depth ?? preset.depth ?? 100,
    maxWeight: row.maxWeight ?? preset.maxWeight ?? 1500,
    name: row.palleteName ?? preset.name ?? cloType,
    type: cloType,
    weight: preset.weight ?? 10,
    size: {
      length: row.length ?? preset.size?.length ?? 0,
      width: row.width ?? preset.size?.width ?? 0,
      height: row.height ?? preset.size?.height ?? 0,
    },
    stacking: {
      fill: preset.stacking?.fill ?? 1,
      layers: preset.stacking?.layers ?? 10,
      maxHeight: preset.stacking?.maxHeight ?? 0,
      ...(preset.stacking ?? {}),
    },
  };
};

export const cloGroupToUiGroup = (group: CloGroup): UiGroup => {
  const usePallets = Boolean(group.pallets && typeof group.pallets === "object");
  const palletRow =
    usePallets && group.pallets && typeof group.pallets === "object"
      ? cloPalletToPalletRow(group.pallets)
      : createDefaultPalletRow();

  return {
    id: String(group.uid),
    name: group.name,
    usePallets,
    cargoRows: group.items.map(cloProductToCargoRow),
    palletRow,
  };
};

export const uiGroupToCloGroup = (group: UiGroup, color = "#000"): CloGroup => {
  const parsed = Number(group.id);
  const uid = Number.isFinite(parsed) ? parsed : hashStringToUid(group.id);

  return {
    name: group.name,
    uid,
    color,
    pallets: group.usePallets ? palletRowToCloPallet(group.palletRow) : false,
    items: group.cargoRows.map(cargoRowToCloProduct),
  };
};

function hashStringToUid(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) || Date.now();
}
